import React, { useEffect, useRef, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { postQuickReply } from 'reducers/farmfeed';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import QuickReplyChip from 'components/QuickReplyChip';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import mixpanel from 'mixpanel-browser';
// styles
import './FarmFeedQuickReplies.scss';

// One-tap replies right above the comment composer; the API resolves labels, target and role access (EPN-3037)
const FarmFeedQuickReplies = ({ activity, currentUser, postQuickReply }) => {
  const [submittingId, setSubmittingId] = useState(null);
  const isMounted = useRef(true);
  const { formatMessage } = useIntl();
  const { id, farm, pig_group, quick_replies = [] } = activity;

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  if (!quick_replies.length) return null;

  // every chip on the row addresses the same person; the helper and tooltip wait for a resolved name
  const { target = {} } = quick_replies[0];
  const firstName = target.first_name || target.full_name;
  const tooltip = target.full_name
    ? formatMessage({ id: 'container.farmfeed.row.quickReply.tooltip' }, { name: target.full_name })
    : undefined;

  const handleTap = (quickReplyId) => {
    if (submittingId) return;
    setSubmittingId(quickReplyId);
    mixpanel.track('Feature Used', {
      user_id: currentUser.id,
      feature_name: 'FarmFeedQuickReplies',
      action: 'Tap Quick Reply',
      quick_reply_id: quickReplyId,
      activity_id: id,
      farm_id: farm?.id,
      group_id: pig_group?.id,
      timestamp: new Date().toISOString(),
    });
    // the chips leave the event on success and this row unmounts, so only reset state while still mounted
    const reset = () => isMounted.current && setSubmittingId(null);
    postQuickReply(id, quickReplyId).then(reset, (errors) => {
      reset();
      toastResponseErrors(errors);
    });
  };

  return (
    <div className="FarmFeedQuickReplies">
      <span className="quick-replies-label">
        <FormattedMessage id="container.farmfeed.row.quickReply.label" />
      </span>
      <div className="quick-replies-scroll">
        {quick_replies.map((reply) => (
          <QuickReplyChip
            key={reply.id}
            disabled={!!submittingId}
            title={tooltip}
            onClick={() => handleTap(reply.id)}
          >
            {reply.label}
          </QuickReplyChip>
        ))}
      </div>
      {firstName && (
        <>
          <span className="quick-replies-helper">
            <svg
              className="helper-icon"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
              <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
            </svg>
            <FormattedMessage id="container.farmfeed.row.quickReply.notifiesOnly" values={{ name: firstName }} />
          </span>
          <span className="quick-replies-helper-mobile">
            <FormattedMessage id="container.farmfeed.row.quickReply.notifiesOnlyMobile" values={{ name: firstName }} />
          </span>
        </>
      )}
    </div>
  );
};

FarmFeedQuickReplies.propTypes = {
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  postQuickReply: T.func.isRequired,
};

export default connect(
  (state) => ({
    currentUser: state.auth.user,
  }),
  { postQuickReply }
)(FarmFeedQuickReplies);
